import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
    FaFileExcel, 
    FaUpload, 
    FaDownload, 
    FaSpinner, 
    FaCheckCircle, 
    FaTimesCircle,
    FaInfoCircle,
    FaArrowLeft
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './BulkEmployeeCreation.css';

const BulkEmployeeCreation = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState(null);
    const baseUrl = "http://localhost:8000";

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const droppedFile = e.dataTransfer.files[0];
        validateAndSetFile(droppedFile);
    };

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        validateAndSetFile(selectedFile);
    };

    const validateAndSetFile = (file) => {
        const validExtensions = ['.xlsx', '.xls'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid File Type',
                text: 'Please upload an Excel file (.xlsx or .xls)',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'Please upload a file smaller than 5MB',
                confirmButtonColor: '#1e3a8a'
            });
            return;
        }

        setFile(file);
        setUploadStatus(null);
    };

    const downloadTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${baseUrl}/api/employees/download-template/`,
                {
                    headers: { 
                        'Authorization': `Bearer ${token}` 
                    },
                    responseType: 'blob'
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'employee_template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error downloading template:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to download template. Please try again.',
                confirmButtonColor: '#1e3a8a'
            });
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        try {
            setUploading(true);
            setUploadProgress(0);
            setUploadStatus(null);

            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${baseUrl}/api/employees/bulk-upload/',`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(progress);
                    }
                }
            );

            setUploadStatus('success');
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Employees created successfully',
                confirmButtonColor: '#1e3a8a'
            }).then(() => {
                navigate('/admin-manager/employees');
            });

        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadStatus('error');
            
            let errorMessage = 'Failed to upload file. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
                confirmButtonColor: '#1e3a8a'
            });
        } finally {
            setUploading(false);
        }
    };

  return (
    <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="bulk-creation-container">
                    <div className="page-header">
                        <div className="header-content">
                            <h1>
                                <FaFileExcel className="header-icon" />
                                Bulk Employee Creation
                            </h1>
                            <p className="header-description">
                                Upload an Excel file to create multiple employees at once
                            </p>
                        </div>
                        <button 
                            className="back-button"
                            onClick={() => navigate('/admin-manager/employees')}
                        >
                            <FaArrowLeft /> Back to Employees
                        </button>
                    </div>

                    <div className="upload-section">
                        <div className="template-download">
                            <h3>Step 1: Download Template</h3>
                            <p>Download the Excel template and fill in the employee details</p>
                            <button 
                                className="download-button"
                                onClick={downloadTemplate}
                            >
                                <FaDownload /> Download Template
                            </button>
                        </div>

                        <div className="file-upload">
                            <h3>Step 2: Upload Filled Template</h3>
                            <div 
                                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept=".xlsx,.xls"
                                    style={{ display: 'none' }}
                                />
                                <FaUpload className="upload-icon" />
                                <p>Drag & Drop your Excel file here or click to browse</p>
                                <span className="file-info">Maximum file size: 5MB</span>
                            </div>

                            {file && (
                                <div className="selected-file">
                                    <FaFileExcel className="file-icon" />
                                    <span>{file.name}</span>
                                    <button 
                                        className="remove-file"
                                        onClick={() => setFile(null)}
                                    >
                                        <FaTimesCircle />
                                    </button>
                                </div>
                            )}

                            {uploading && (
                                <div className="upload-progress">
                                    <div 
                                        className="progress-bar"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                    <span>{uploadProgress}%</span>
                                </div>
                            )}

                            {uploadStatus && (
                                <div className={`upload-status ${uploadStatus}`}>
                                    {uploadStatus === 'success' ? (
                                        <>
                                            <FaCheckCircle />
                                            <span>Upload successful!</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaTimesCircle />
                                            <span>Upload failed. Please try again.</span>
                                        </>
                                    )}
                                </div>
                            )}

                            <button 
                                className="upload-button"
                                onClick={handleUpload}
                                disabled={!file || uploading}
                            >
                                {uploading ? (
                                    <>
                                        <FaSpinner className="spinner" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <FaUpload />
                                        Upload File
                                    </>
                                )}
                            </button>
                        </div>

                        <div className="upload-guidelines">
                            <h3>Guidelines</h3>
                            <ul>
                                <li>
                                    <FaInfoCircle />
                                    Use the provided template for correct data format
                                </li>
                                <li>
                                    <FaInfoCircle />
                                    Ensure all required fields are filled
                                </li>
                                <li>
                                    <FaInfoCircle />
                                    Maximum file size is 5MB
                                </li>
                                <li>
                                    <FaInfoCircle />
                                    Only .xlsx and .xls files are supported
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkEmployeeCreation;